import React from 'react'
import Backdrop from '@mui/material/Backdrop';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { Box, useTheme, Typography } from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Autocomplete from '@mui/material/Autocomplete';
import { tokens } from "../../../theme";
import dayjs from 'dayjs';
import Axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import { getAuthHeader } from '../../../utils/authToken';

const UpdateStudentModal = ({ 
    setAnchorEl, 
    course, 
    setInfoUpdated,
    setBackdropOpen,
    setSuccessAlertOpen,
    setSuccessAlertMessage,
    setErrorAlertOpen,
    setErrorAlertMessage
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // for modal
    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => { setOpen(false); setAnchorEl(null); };

    // for student to update
    const [student, setStudent] = React.useState(null);

    // for start and end date to add a student
    const [startDate, setStartDate] = React.useState(dayjs().startOf('day').locale('en'));
    const [endDate, setEndDate] = React.useState(dayjs().endOf('day').locale('en'));

    const handleUpdateStudent = async () => {
        if (student === null) {
            setErrorAlertMessage("Please select a student to update.");
            setErrorAlertOpen(true);
            return;
        }

        if (startDate === null) {
            setErrorAlertMessage("Please select a start date.");
            setErrorAlertOpen(true);
            return;
        }

        if (endDate === null) {
            setErrorAlertMessage("Please select an end date.");
            setErrorAlertOpen(true);
            return;
        }

        if (startDate.isAfter(endDate)) {
            setErrorAlertMessage("Start date cannot be after end date.");
            setErrorAlertOpen(true);
            return;
        }

        if (startDate.endOf('day').isBefore(dayjs(course.start_date)) || endDate.startOf('day').isAfter(dayjs(course.end_date))) {
            setErrorAlertMessage("Start and end date must be within course's start and end date.");
            setErrorAlertOpen(true);
            return;
        }

        setBackdropOpen(true);
        await Axios.put(`${process.env.REACT_APP_URL}/api/v1/courses/${course.uuid}/students/${student.uuid}`, {
            start_date: startDate,
            end_date: endDate
        }, {
            headers: {
                Authorization: getAuthHeader(),
            },
        }).then((response) => {
            setSuccessAlertMessage(response.data.message);
            setSuccessAlertOpen(true);
            handleClose();
            setTimeout(() => {
                setBackdropOpen(false);
                setInfoUpdated(true);
            }, 2000);
        }).catch((error) => {
            setErrorAlertMessage(error.response?.data?.message || error.response?.data?.error || "An error occurred.");
            setErrorAlertOpen(true);
            setBackdropOpen(false);
        });
    };

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 500,
        maxHeight: '90vh',
        overflowY: 'auto',
        bgcolor: colors.primary[600],
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
    };

    return (
        <div>
            <Button onClick={handleOpen} sx={{ p: 1, width: "100%" }}>Update Student</Button>
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                open={open}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <Fade in={open}>
                    <Box sx={style}>
                        <Typography variant="h4" color={colors.primary[450]}>
                            Update Student's Schedule
                        </Typography>
                        <CloseIcon sx={{ position: "absolute", right: 10, top: 10, cursor: "pointer" }} onClick={handleClose} />
                        <Box display="flex" flexDirection="column" gap={3} width="100%" pt={2}>
                                <Autocomplete
                                    disablePortal
                                    id="combo-box-demo-1"
                                    getOptionLabel={(option) => option.student.consultation_number ?
                                        `${option.student.first_name} ${option.student.korean_name !== null ? option.student.korean_name : ''} ${option.student.last_name} (Consult #: ${option.student.consultation_number})`
                                        : `${option.student.first_name} ${option.student.korean_name !== null ? option.student.korean_name : ''} ${option.student.last_name}`}
                                    getOptionKey={(option) => option.id}
                                    renderOption={(props, option) => (
                                        <li {...props}>
                                            {option.student.first_name} {option.student.korean_name !== null ? option.student.korean_name : ''} {option.student.last_name}
                                            {option.student.consultation_number && (
                                                <>
                                                    &nbsp;(<span style={{ color: "green" }}> Consult #: {option.student.consultation_number}</span>)
                                                </>
                                            )}
                                        </li>
                                    )}
                                
                                    options={ 
                                        course.student_courses !== null && course.student_courses.length > 0 
                                        ? course.student_courses
                                        : []
                                    }
                                    renderInput={(params) => <TextField {...params} label="Select a student" />}
                                    onChange={
                                        (event, value) => {
                                            setStudent(value.student);
                                            setStartDate(dayjs(value.start_date).locale('en').startOf('day'));
                                            setEndDate(dayjs(value.end_date).locale('en').endOf('day'));
                                        }
                                    }
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    sx={{ width: "100%", pr: "5rem" }}
                                />
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="From"
                                        value={startDate}
                                        onChange={(newValue) => setStartDate(newValue)}
                                        sx={{ width: "100%", pr: "5rem" }}
                                    />
                                </LocalizationProvider>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="To"
                                        value={endDate}
                                        onChange={(newValue) => setEndDate(newValue)}
                                        sx={{ width: "100%", pr: "5rem" }}
                                    />
                                </LocalizationProvider>
                                <Button
                                    type="button"
                                    variant="contained"
                                    color="primary"
                                    sx={{ mt: 2, width: "7rem" }}
                                    onClick={handleUpdateStudent}
                                >
                                    Update
                                </Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </div>
    )
}

export default UpdateStudentModal